/* @jsx h */
import { defineComponent, ref, h, computed } from "vue";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";

import { useCheckout, useShop, useAuth } from "../../providers";
import {
  Promotion,
  AppliedPromotion,
  PromotionReward,
  CheckoutData,
} from "@lemmonhq/types";
import {
  evaluateLoyaltySelection,
  isLoyaltyPromotion,
  calculateApplicablePromotions,
  calculatePromotionApplications,
  calculatePromotionValue,
  formatPromotionDescription,
  normalizeLoyaltyRewardSelections,
} from "@lemmonhq/orders";

interface PromotionsModalProps {
  onClose: () => void;
}

// Helper function to format reward description
const formatRewardDescription = (
  reward: PromotionReward,
  currency: string,
): string => {
  switch (reward.type) {
    case "order_discount":
      return reward.mode === "percentage"
        ? `${reward.value}% off order`
        : `${currency} ${reward.value} off order`;
    case "product_discount":
      return reward.mode === "percentage"
        ? `${reward.value}% off qualifying items`
        : `${currency} ${reward.value} off qualifying items`;
    case "product":
      return reward.quantity > 1 ? `${reward.quantity}x` : "1x";
    default:
      return "OFF";
  }
};

const formatRewardShort = (
  reward: PromotionReward,
  currency: string,
): string => {
  switch (reward.type) {
    case "order_discount":
      return reward.mode === "percentage"
        ? `${reward.value}%`
        : `${currency} ${reward.value}`;
    case "product_discount":
      return reward.mode === "percentage"
        ? `${reward.value}%`
        : `${currency} ${reward.value}`;
    case "product":
      return reward.quantity > 1 ? `${reward.quantity}x` : "1x";
    default:
      return "OFF";
  }
};

const formatRewardLabel = (reward: PromotionReward): string => {
  switch (reward.type) {
    case "order_discount":
      return "OFF ORDER";
    case "product_discount":
      return "OFF ITEMS";
    case "product":
      return "FREE ITEM";
    default:
      return "SPECIAL";
  }
};

export const PromotionsModal = defineComponent<PromotionsModalProps>({
  name: "PromotionsModal",
  props: ["onClose"],
  setup(props) {
    const { t } = useLocalization();
    const shop = useShop();
    const checkout = useCheckout();
    const auth = useAuth();

    const allPromotions = computed(() => {
      const promotions = (shop.getPromotions() || []).filter((p) => p.enabled);
      return [...promotions].sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      );
    });

    const selectedPromotions = ref<Set<string>>(new Set());

    const appliedPromotions = computed(() => {
      if (!checkout.checkout.value) return [];
      return checkout.checkout.value.promotions || [];
    });

    const initializeSelected = () => {
      selectedPromotions.value = new Set(
        appliedPromotions.value.map((p) => p.promotion_id),
      );
    };
    initializeSelected();

    const applicablePromotions = computed(() => {
      if (!checkout.checkout.value) return [];
      return calculateApplicablePromotions(
        shop.services.value,
        checkout.checkout.value,
        allPromotions.value,
      );
    });

    // Capped membership rewards the identified customer holds, keyed by program.
    // They are manual-only (excluded from auto-apply) and surfaced here for
    // explicit selection with their remaining allowance. Advisory numbers — the
    // authoritative check is the locked reservation at payment time.
    // See docs/plans/loyalty/phase_1_capped_reward_checkout.md.
    const cappedMembershipsByProgram = computed(() => {
      const memberships = checkout.checkout.value?.customer?.memberships ?? [];
      const map = new Map<string, (typeof memberships)[number]>();
      for (const m of memberships) {
        if (m.cap_count != null || m.cap_amount != null)
          map.set(m.program_id, m);
      }
      return map;
    });

    const membershipForPromo = (promotion: Promotion) =>
      promotion.membership_program_id
        ? cappedMembershipsByProgram.value.get(promotion.membership_program_id)
        : undefined;

    const membershipAllowanceText = (m: {
      cap_count: number | null;
      cap_amount: number | null;
      remaining: number | null;
      budget_remaining: number | null;
    }): string => {
      const currency = auth.user.value?.restaurant.currency || "CHF";
      const parts: string[] = [];
      if (m.cap_count != null) {
        const left = m.remaining ?? 0;
        parts.push(`${left} use${left === 1 ? "" : "s"} left`);
      }
      if (m.cap_amount != null) {
        parts.push(`${currency} ${(m.budget_remaining ?? 0).toFixed(2)} left`);
      }
      return parts.join(" · ");
    };

    const togglePromotion = (promotionId: string) => {
      const next = new Set(selectedPromotions.value);
      if (next.has(promotionId)) {
        next.delete(promotionId);
      } else {
        next.add(promotionId);
      }
      selectedPromotions.value = next;
    };

    const ordinarySelection = computed(() =>
      Array.from(selectedPromotions.value).flatMap((id) => {
        const p = allPromotions.value.find((p) => p.id === id);
        if (!p || isLoyaltyPromotion(p)) return [];
        const apps = applicablePromotions.value.filter(
          (a) => a.promotion_id === id,
        );
        return apps.length
          ? apps
          : p.requirements.length === 0
            ? calculatePromotionApplications(
                shop.services.value,
                p,
                checkout.checkout.value!,
              )
            : [];
      }),
    );
    const loyaltySelections = computed(() => {
      const existing = new Map(
        normalizeLoyaltyRewardSelections(
          checkout.checkout.value?.customer?.reward_selections,
        ).map((selection) => [selection.promotion_id, selection]),
      );
      return Array.from(selectedPromotions.value)
        .filter((id) =>
          allPromotions.value.some(
            (promotion) => promotion.id === id && isLoyaltyPromotion(promotion),
          ),
        )
        .map((id) => existing.get(id) ?? { promotion_id: id, quantity: 1 });
    });
    const loyaltyEvaluation = computed(() =>
      evaluateLoyaltySelection(
        shop.services.value,
        checkout.checkout.value!,
        allPromotions.value,
        loyaltySelections.value,
        ordinarySelection.value,
      ),
    );

    const getPromotionValue = (promotionId: string): number => {
      const loyalty = loyaltyEvaluation.value.options.find(
        (o) => o.promotion.id === promotionId,
      );
      if (loyalty) return loyalty.value;
      const applicable = applicablePromotions.value.filter(
        (promotion) => promotion.promotion_id === promotionId,
      );
      if (applicable.length > 0) {
        return applicable.reduce(
          (sum, application) => sum + application.value,
          0,
        );
      }

      const promotion = allPromotions.value.find((p) => p.id === promotionId);
      if (promotion && checkout.checkout.value) {
        if (!promotion.requirements || promotion.requirements.length === 0) {
          return calculatePromotionValue(
            shop.services.value,
            promotion,
            checkout.checkout.value,
          );
        }
      }
      return 0;
    };

    const applySelectedPromotions = () => {
      if (!checkout.checkout.value) return;

      const newAppliedPromotions = [
        ...ordinarySelection.value,
        ...loyaltyEvaluation.value.applications,
      ];

      checkout.setCheckout({
        ...checkout.checkout.value,
        promotions: newAppliedPromotions,
        customer: checkout.checkout.value.customer
          ? {
              ...checkout.checkout.value.customer,
              reward_selections: loyaltySelections.value,
            }
          : undefined,
      } as CheckoutData);
    };

    const promotionGroups = computed(() => {
      const applicable = new Set(
        applicablePromotions.value.map((p) => p.promotion_id),
      );
      const selected = selectedPromotions.value;

      const selectedPromos: Promotion[] = [];
      const applicablePromos: Promotion[] = [];
      const manualPromos: Promotion[] = [];
      const membershipPromos: Promotion[] = [];
      const otherPromos: Promotion[] = [];

      allPromotions.value.forEach((promo) => {
        // Capped membership rewards the customer holds get their own section
        // (selected state is reflected there), whether or not they're selected.
        if (isLoyaltyPromotion(promo)) {
          if (
            loyaltyEvaluation.value.options.some(
              (o) => o.promotion.id === promo.id,
            )
          )
            membershipPromos.push(promo);
        } else if (selected.has(promo.id)) selectedPromos.push(promo);
        else if (applicable.has(promo.id)) applicablePromos.push(promo);
        else if (!promo.requirements || promo.requirements.length === 0)
          manualPromos.push(promo);
        else otherPromos.push(promo);
      });

      return {
        selected: selectedPromos,
        available: applicablePromos,
        manual: manualPromos,
        membership: membershipPromos,
        other: otherPromos,
      };
    });

    const totalSavings = computed(() =>
      Array.from(selectedPromotions.value).reduce(
        (sum, id) => sum + getPromotionValue(id),
        0,
      ),
    );

    const isPromotionApplicable = (promotionId: string) =>
      applicablePromotions.value.some((p) => p.promotion_id === promotionId);

    const renderCouponCard = (promotion: Promotion, canSelect: boolean) => {
      const isSelected = selectedPromotions.value.has(promotion.id);
      const loyalty = loyaltyEvaluation.value.options.find(
        (o) => o.promotion.id === promotion.id,
      );
      if (loyalty) canSelect = isSelected || !loyalty.reason;
      const isApplicable = isPromotionApplicable(promotion.id);
      const value = getPromotionValue(promotion.id);
      const membership = membershipForPromo(promotion);

      // A single notch on either side creates the familiar coupon silhouette.
      const couponMask =
        "radial-gradient(circle at left center, transparent 11px, black 11.5px), radial-gradient(circle at right center, transparent 11px, black 11.5px)";

      // Status text — always rendered at fixed height to prevent layout shift
      const statusText = (() => {
        if (loyalty?.reason) return t(`loyalty.rewards.${loyalty.reason}`);
        if (!canSelect)
          return membership ? "No allowance left" : "Conditions not met";
        if (value > 0)
          return `−${auth.user.value?.restaurant.currency || "CHF"} ${value.toFixed(2)}`;
        if (isSelected && value === 0) return "Applies when eligible";
        if (membership) return "Select to apply";
        if (isApplicable) return "Available now";
        return "";
      })();

      return (
        <div
          key={promotion.id}
          class={`relative transition-all duration-200 ${canSelect ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}`}
          style={{ padding: "0 11px" }}
          onClick={() => canSelect && togglePromotion(promotion.id)}
        >
          <div
            class={`flex rounded-2xl overflow-hidden shadow-md transition-all duration-200 ${
              isSelected
                ? "shadow-primary/30 ring-2 ring-primary"
                : "shadow-base-300/40"
            }`}
            style={{
              maskImage: couponMask,
              WebkitMaskImage: couponMask,
              maskSize: "51% 100%",
              WebkitMaskSize: "51% 100%",
              maskPosition: "left top, right top",
              WebkitMaskPosition: "left top, right top",
              maskRepeat: "no-repeat",
              WebkitMaskRepeat: "no-repeat",
            }}
          >
            {/* Left stamp — fixed width, bg-primary */}
            <div class="bg-primary text-primary-content flex flex-col items-center justify-center py-5 px-5 shrink-0 w-40">
              <span class="text-2xl font-black leading-none w-full text-center px-1 truncate">
                {formatRewardShort(
                  promotion.reward,
                  auth.user.value?.restaurant.currency || "CHF",
                )}
              </span>
              <span class="text-[9px] font-bold tracking-widest mt-1 opacity-80 w-full text-center px-1 truncate">
                {formatRewardLabel(promotion.reward)}
              </span>
            </div>

            {/* Dashed separator */}
            <div class="bg-accent shrink-0" style={{ width: "1px" }}>
              <div
                class="h-full w-full"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(to bottom, transparent, transparent 4px, rgba(0,0,0,0.2) 4px, rgba(0,0,0,0.2) 8px)",
                }}
              />
            </div>

            {/* Right content section */}
            <div
              class="bg-accent text-accent-content flex-1 flex items-center justify-between px-4 py-4 gap-3 min-w-0"
              style={{
                backgroundImage:
                  "radial-gradient(circle, oklch(var(--ac) / 0.06) 1.5px, transparent 1.5px), radial-gradient(circle, oklch(var(--ac) / 0.06) 1.5px, transparent 1.5px)",
                backgroundSize: "16px 16px",
                backgroundPosition: "0 0, 8px 8px",
              }}
            >
              <div class="flex-1 min-w-0">
                <p class="font-bold text-sm leading-tight truncate">
                  {promotion.name}
                </p>
                <p class="text-xs opacity-70 mt-0.5 line-clamp-2 leading-snug">
                  {formatPromotionDescription(promotion)}
                </p>
                {/* Fixed-height status line — no layout shift on toggle */}
                <p
                  class={`text-xs mt-2 h-4 leading-none font-semibold ${
                    value > 0
                      ? "text-accent-content/90"
                      : "text-accent-content/50"
                  }`}
                >
                  {statusText}
                </p>
                {membership && (
                  <p class="text-[10px] mt-1 leading-none opacity-60">
                    {membershipAllowanceText(membership)}
                  </p>
                )}
              </div>

              {/* Checkbox */}
              {canSelect && (
                <div
                  class={`shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                    isSelected
                      ? "bg-accent-content border-accent-content text-accent"
                      : "border-accent-content/40 bg-transparent"
                  }`}
                >
                  {isSelected && (
                    <span class="material-symbols-outlined text-sm">check</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };

    const SectionLabel = ({ label }: { label: string }) => (
      <p class="text-xs font-bold uppercase tracking-widest text-base-content/40 px-1 mb-2">
        {label}
      </p>
    );

    return () => {
      const groups = promotionGroups.value;
      const hasAny = allPromotions.value.length > 0;

      return (
        <LemmonModal
          title="Rewards & Promotions"
          onClose={props.onClose}
          renderButtons={() => (
            <div class="p-4 border-t bg-base-100">
              <div class="flex items-center justify-between gap-3">
                <div class="w-44 shrink-0 h-12 flex items-center">
                  {totalSavings.value > 0 ? (
                    <div class="flex items-center gap-2">
                      <span class="material-symbols-outlined text-success text-lg">
                        savings
                      </span>
                      <div>
                        <div class="text-xs text-base-content/50 leading-none mb-0.5">
                          Total savings
                        </div>
                        <div class="font-bold text-success leading-tight">
                          −{auth.user.value?.restaurant.currency || "CHF"}{" "}
                          {totalSavings.value.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div class="text-sm text-base-content/50">
                      {selectedPromotions.value.size > 0
                        ? `${selectedPromotions.value.size} selected`
                        : "No promotions selected"}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    applySelectedPromotions();
                    props.onClose();
                  }}
                  disabled={loyaltyEvaluation.value.options.some(
                    (o) =>
                      selectedPromotions.value.has(o.promotion.id) &&
                      !!o.reason,
                  )}
                  class="btn btn-primary gap-1.5 px-5 flex-1 md:max-w-[200px]"
                  type="button"
                >
                  <span class="material-symbols-outlined text-sm">check</span>
                  Apply
                </button>
              </div>
            </div>
          )}
        >
          <div class="overflow-y-auto max-h-[65vh] px-2 py-4 space-y-6">
            {!hasAny && (
              <div class="flex flex-col items-center justify-center py-16 text-center">
                <div class="w-16 h-16 rounded-full bg-base-200 flex items-center justify-center mb-4">
                  <span class="material-symbols-outlined text-3xl text-base-content/30">
                    local_offer
                  </span>
                </div>
                <p class="font-semibold text-base-content/70">
                  No promotions available
                </p>
                <p class="text-sm text-base-content/40 mt-1">
                  Check back later for deals.
                </p>
              </div>
            )}

            {groups.selected.length > 0 && (
              <div>
                <SectionLabel label="Selected" />
                <div class="space-y-3">
                  {groups.selected.map((p) => renderCouponCard(p, true))}
                </div>
              </div>
            )}

            {groups.membership.length > 0 && (
              <div>
                <SectionLabel label="Customer rewards" />
                <div class="space-y-3">
                  {groups.membership.map((p) => {
                    return renderCouponCard(p, true);
                  })}
                </div>
              </div>
            )}

            {groups.available.length > 0 && (
              <div>
                <SectionLabel label="Available now" />
                <div class="space-y-3">
                  {groups.available.map((p) => renderCouponCard(p, true))}
                </div>
              </div>
            )}

            {groups.manual.length > 0 && (
              <div>
                <SectionLabel label="Apply manually" />
                <div class="space-y-3">
                  {groups.manual.map((p) => renderCouponCard(p, true))}
                </div>
              </div>
            )}

            {groups.other.length > 0 && (
              <div>
                <SectionLabel label="Not yet eligible" />
                <div class="space-y-3">
                  {groups.other.map((p) => renderCouponCard(p, false))}
                </div>
              </div>
            )}
          </div>
        </LemmonModal>
      );
    };
  },
});
